import React, { useEffect, useState } from "react";
import { Star, Crown, Medal, Award } from "lucide-react";
import Reveal from "@/components/Reveal";
import { Container, SectionHeader } from "@/components/Section";
import { api } from "@/lib/api";

const rankStyle = (rank) => {
  if (rank === 1) return { ring: "ring-amber-400", badge: "bg-amber-400 text-amber-950", Icon: Crown, glow: "shadow-amber-200" };
  if (rank === 2) return { ring: "ring-slate-300", badge: "bg-slate-300 text-slate-800", Icon: Medal, glow: "shadow-slate-200" };
  if (rank === 3) return { ring: "ring-orange-300", badge: "bg-orange-300 text-orange-900", Icon: Award, glow: "shadow-orange-200" };
  return { ring: "ring-sky-200", badge: "bg-blue-900 text-white", Icon: Star, glow: "shadow-sky-100" };
};

export default function TopStars() {
  const [data, setData] = useState({ enabled: true, students: [] });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/leaderboard/public").then((r) => setData(r.data)).catch(() => setData({ enabled: false, students: [] })).finally(() => setLoaded(true));
  }, []);

  if (!loaded || !data.enabled || !data.students || data.students.length === 0) return null;

  const top3 = data.students.slice(0, 3);
  const rest = data.students.slice(3);

  return (
    <section className="py-16 sm:py-24 bg-blue-950 text-white relative overflow-hidden" data-testid="public-leaderboard-section">
      <div className="ptg-grid-bg absolute inset-0 opacity-20" />
      <Container className="relative">
        <SectionHeader light eyebrow="Star Achievers" title="PTG Top 10 Star Holders"
          subtitle="Celebrating the students earning the most stars for effort, discipline and progress across all standards." />

        {/* Podium — top 3 */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 max-w-4xl mx-auto">
          {top3.map((s, i) => {
            const st = rankStyle(s.rank);
            return (
              <Reveal key={`${s.name}-${i}`} delay={i * 0.08}>
                <div className={`rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm p-6 text-center ring-1 ${st.ring} ${s.rank === 1 ? "sm:-translate-y-3" : ""}`} data-testid={`leaderboard-top-${i}`}>
                  <div className={`mx-auto flex h-12 w-12 items-center justify-center rounded-full ${st.badge}`}>
                    <st.Icon className="h-6 w-6" />
                  </div>
                  <p className="mt-3 text-xs font-bold uppercase tracking-widest text-sky-300">Rank {s.rank}</p>
                  <p className="mt-1 text-lg font-extrabold leading-tight">{s.name}</p>
                  <p className="text-sm text-slate-300">{s.standard}</p>
                  <p className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-amber-400/15 border border-amber-400/30 px-3 py-1 font-bold text-amber-300">
                    <Star className="h-4 w-4 fill-amber-400 text-amber-400" /> {s.star_balance}
                  </p>
                </div>
              </Reveal>
            );
          })}
        </div>

        {/* Rows 4-10 */}
        {rest.length > 0 && (
          <div className="mt-6 max-w-3xl mx-auto space-y-2" data-testid="leaderboard-rest">
            {rest.map((s, i) => (
              <div key={`${s.name}-${i}`} className="flex items-center gap-4 rounded-xl bg-white/5 border border-white/10 px-4 py-3" data-testid={`leaderboard-row-${i}`}>
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/10 font-bold text-sky-200">{s.rank}</span>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold truncate">{s.name}</p>
                  <p className="text-xs text-slate-400">{s.standard}</p>
                </div>
                <span className="flex items-center gap-1.5 font-bold text-amber-300"><Star className="h-4 w-4 fill-amber-400 text-amber-400" /> {s.star_balance}</span>
              </div>
            ))}
          </div>
        )}
      </Container>
    </section>
  );
}
